<template>
<div>
    <div 
        v-if="activeStory"
        :class="[`page-inner story story--${activeStory.theme}`, 'story__wrap']"
    >
        <div :class="['stories', {
            'stories--recruit': $route.path.includes('recruit-')
        }]" ref="wrapper">

            <div class="wrapper--story-button">
                <arrow-button
                    type="top"
                    text="Back"
                    :label="backButton.label"
                    @click.native="() => $router.push(backButton.route)"
                />
            </div>

            <transition appear name="load" mode="out-in">
                <story-item
                    v-if="activeStory"
                    :ref="'story-' + slugPath"
                    :key="visibleClass"
                    :story="activeStory"
                    :slug="slugPath(activeStory)"
                    :group="group"
                />
            </transition>
            
        </div>

        <PageCtas />
        <PageFooter />
        
    </div>
    
    <Lines />

    <template v-if="activeStory">
        <component
            :is="'VideoFullscreen'"
            :key="'story-video-'"
            v-if="Array.isArray(activeStory.images) && activeStory.images[0] && activeStory.images[0].type == 'video'"
            :name="activeStory.images[0].media"
            :media="activeStory.images[0].media"
        />
    </template>
</div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import gsap from 'gsap'

// import { focusables } from '@/mixins/focusables'
// import { focusOrder } from '@/mixins/focusOrder'
import initFullscreenVideo from '@/mixins/lib/initFullscreen'

import StoryItem from './StoryItem'
import SlideLines from '../Slide/SlideLines'
import VideoFullscreen from '../Ui/VideoFullscreen'

import { BACK_BUTTON_LINK } from '@/data/pages'
import { focusOrderWatcher } from '@/mixins/focusOrder'

import PageCtas from '../Discover/PageCtas'
import PageFooter from '../PageFooter.vue'

function destructureSlug(text, del = '-') {
    const parts = text.split('-')

    const nameParts = parts.slice(-2)
    const slugParts = parts.slice(0, -2)

    return {
        name: nameParts.join(del),
        slug: slugParts.join(del),
    }
}

export default {
    name: 'Stories',
    props: ['slug', 'storyId', 'parentSlug'],

    metaInfo() {
        const config = {
            title: '',
            image: null
        }
        if ( this.activeStory ) {
            const { title } = this.activeStory
            config.title = title.replaceAll('<br>', ' ')
        }
        return {
            title: config.title,
        }
    },

    components: {
        StoryItem,
        VideoFullscreen,
        Lines: SlideLines,
        PageCtas,
        PageFooter,
    },

    mixins: [
        focusOrderWatcher,
    ],

    data() {
        return {
            activeStory: null,
            activeElement: null,
            visibleClass: '',
            routeSlug: '',
            direct: true,
        }
    },

    watch: {
        '$route'(path) {
            if (!this.isReady) {
                this.visibleClass = 'start'
                requestAnimationFrame(() => {
                    this.visibleClass = ''
                })
                return;
            }
            const next = () => {
                this.visibleClass = 'start'
                this.updateStoryBySlug( path )
                requestAnimationFrame(() => {
                    this.visibleClass = ''
                })
            }
            gsap.to( document.body, {
                scrollTop: 0,
                duration: .75,
                ease: 'expo',
                onComplete: next
            })
        },
    },

    beforeRouteEnter (to, from, next) {
        gsap.set( document.body, {
            scrollTop: 0,
            onComplete: next
        })
    },

    activated() {
        this.isReady = true
        this.resolveStoryBySlug()
        this.rootClass('rm', 'no-overflow')

        const vm = this

        this.$root.$on('fullscreen-play', id => {

            initFullscreenVideo.call(vm, id, () => {
                vm.activeElement = document.activeElement
            }, () => {
                vm.activeElement && vm.activeElement.focus()
            })
        })
    },

    deactivated () {
        this.isReady = false
        this.activeStory = null
        this.$root.$off('fullscreen-play')
    },

    methods: {
        getAuthorName( story ) {
            if (story.author) {
                return story.author.name.trim().toLowerCase()
            }
            return ''
        },
        getUrlParams( route ) {
            const fullSlug = route.split('/')[2]
            const { slug, name } = destructureSlug(fullSlug)

            return { slug, name }
        },
        updateStoryBySlug( route ) {
            const { name } = this.getUrlParams(route.path)

            const story = this.group
                .find(story => this.getAuthorName(story) === name.replace(/-/g, ' '))

            this.activeStory = story
        },
        resolveStoryBySlug() {
            if (!this.stories || !Object.keys(this.stories).length) return

            const { slug, name } = this.getUrlParams(this.$route.path)

            const storyGroup = this.storyGroup(this.slugException(slug))
            const { name: groupName, grouped, student, experienced } = this.stories[storyGroup]

            const group = [...grouped]
                .flatMap(group => group)
                .filter(group => group.slug === this.slugException(slug))
            
            const story = group
                .find(story => this.getAuthorName(story) === name.replace(/-/g, ' '))

            if (this.moduleType === 'recruit') {
                this.group = grouped
            } 
            else {
                this.group = this.isUserStudent
                    ? student.flat()
                    : experienced.flat()
            }
            this.routeSlug = groupName
            this.visibleClass = ''
            this.activeStory = story
        },
        slugException( slug ) {
            if (slug === 'create-your') {
                return 'create-your-own-story'
            }
            return slug
        },
        slugPath( story ) {
            if (this.moduleType === 'recruit') {
                return 'recruit' 
            }
            const base = this.routeSlug
            if (base) {
                return `${base}/${story.slug.split('-')[0]}`
            }
            return `${story.slug.split('-')[0]}`
        },
        storyGroup(id) {
            const { moduleType } = this
            return this.$store.getters[`${moduleType}/storyGroup`](id)
        },
        setPreloadImg() {
            if (document.getElementById('storyimg')) {
                document.getElementById('storyimg').remove()
            }

            const { images } = this.activeStory
            const link = document.createElement('link')

            link.rel = 'preload'
            link.fetchpriority = 'high'
            link.href = `/static/stories/${this.slugPath(this.activeStory)}/story/${images[0].media}.png`
            link.as = 'image'
            link.type = 'image/png'
            link.id = 'storyimg'

            const titleElement = document.querySelector('head title')
            titleElement && titleElement.parentNode.insertBefore(link, titleElement.nextSibling)
        },
    },

    provide() {
        return{
            moduleType: this.moduleType,
        }
    },

    computed: {
        backButton() {
            const button = BACK_BUTTON_LINK[this.moduleType] || BACK_BUTTON_LINK.default
            return {
                label: `Back to ${button.title}`,
                route: button.url,
            }
        },
        moduleType() {
            return !this.$route.path.includes('recruit') 
                ? 'discover'
                : 'recruit'
        },
        ...mapState({
            stories(state, getters) {
                return getters[`${this.moduleType}/stories`]
            },
        }),
        ...mapGetters({
            browserSize: 'browser/size',
            isUserStudent: 'isUserStudent'
        })
    },
}
</script>
